/**
 * Styles
 */
import './style.scss';

/**
 * External Dependencies
 */
import React, { Component } from 'react';
import axios from 'axios';
import classnames from 'classnames/dedupe';
import { connect } from 'react-redux';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Row, Col, Button, Modal } from 'reactstrap';

/**
 * Internal Dependencies
 */
import { updateAuth as actionUpdateAuth } from '../../actions';
import { currentUser as updateCurrentUser } from '../../actions/users';
import { changeAccountMode as changeModeAccount } from '../../actions/account-mode';
import { copyToClipboard, getLatestRolePathConfig, paymeUrl } from '../../utils';
import '../../../assets/js/yaybar/yaybar';
import { initPluginYaybar } from '../../../assets/js/rootui-parts/initPluginYaybar';
import Icon from '../icon';
import ModalBodyEmbedButton from '../modals/modal-body-embed-button';
import ModalTutorial from '../modals/modal-tutorial';
import AccountModeButton from './components/account-mode-button';
import { isPaidPlan } from '../../pages/Settings/configs/plan-config';
import { isInternalUserOnly, isSpecialAffiliate } from '../../pages/Settings/configs/role-config';
import SidebarScrollIndicator from './components/sidebar-scroll-indicator';
import { deleteAuthCookies } from '../../utils/auth';

window.RootUI.initPluginYaybar = initPluginYaybar;

// Anchor sidebar yang dipakai langkah Joyride (tour Dashboard + tur menu). Urutannya mengikuti
// `settings.navigation_sidebar` supaya mudah disandingkan.
//
// SATU ROUTE = SATU ID, dan tidak ada dua route yang boleh berbagi id: Joyride mencari target
// lewat `document.querySelector`, jadi id kembar selalu menyorot item yang lebih dulu terdaftar di
// `settings.jsx` — bukan item yang route-nya dituju langkah itu. Karena itu '/webinars' dan
// '/writings' punya id sendiri, terpisah dari '/events' dan '/ebooks'.
//
// ANCHOR SUBMENU ('/api-keys', '/webhook' — anak '/developer-tools'). Diverifikasi task-034 dengan
// merender sidebar sungguhan: anchor anak submenu SELALU ada di DOM, terbuka maupun tertutup.
// `renderSubmenus` merender `<ul class="yay-submenu">` tanpa syarat; yang berubah saat submenu
// dibuka hanyalah kelas `yay-submenu-open` pada `<li>` induk, dan kelas itu murni CSS
// (`assets/css/yaybar/parts/_base.scss`) — plugin yaybar cuma slideUp/slideDown, tidak pernah
// melepas node. Atributnya menempel pada `<a href="/api-keys">` anak, BUKAN pada toggle induk
// (toggle induk memakai id sendiri, `sidebar-developer-tools`, dan `to="#"` sehingga href-nya bukan
// route). Konsekuensi untuk langkah tur: `document.querySelector` tetap menemukan anchor submenu
// yang tertutup, jadi penyaringan langkah di `guided-product-tour.jsx` TIDAK menyaringnya dan
// tooltip bisa jatuh di elemen `display: none`. Langkah yang menyorot anchor submenu wajib membuka
// menu induknya lebih dulu.
const TOUR_IDS_BY_URL = {
  '/': 'sidebar-home',
  '/customers': 'sidebar-customers',
  '/payments': 'sidebar-payments',
  '/invoices': 'sidebar-invoices',
  '/all-products': 'sidebar-all-products',
  '/payment-link': 'sidebar-payment-link',
  '/physical-products': 'sidebar-physical-products',
  '/digital-products': 'sidebar-digital-products',
  '/online-courses': 'sidebar-online-courses',
  '/cohort-courses': 'sidebar-cohort-courses',
  '/webinars': 'sidebar-webinars',
  '/events': 'sidebar-events',
  '/coachings': 'sidebar-coachings',
  '/fundraisings': 'sidebar-fundraisings',
  '/ebooks': 'sidebar-ebooks',
  '/podcasts': 'sidebar-podcasts',
  '/audiobooks': 'sidebar-audiobooks',
  '/writings': 'sidebar-writings',
  '/comics': 'sidebar-comics',
  '/memberships': 'sidebar-memberships',
  '/license-codes': 'sidebar-license-codes',
  '/credit-usage-products': 'sidebar-credit-usage-products',
  '/affiliates': 'sidebar-affiliates',
  '/as-affiliate': 'sidebar-as-affiliate',
  '/balance': 'sidebar-balance',
  '/verification': 'sidebar-verification',
  '/settings': 'sidebar-settings',
  '/developer-tools': 'sidebar-developer-tools',
  '/api-keys': 'sidebar-api-keys',
  '/webhook': 'sidebar-webhook'
};

/**
 * Component
 */
class PageYaybar extends Component {
  constructor(props) {
    super(props);

    this.sidebarRef = React.createRef();

    this.renderSubmenus = this.renderSubmenus.bind(this);
    this.logOut = this.logOut.bind(this);
    this.handleCopy = this.handleCopy.bind(this);

    this.toggle1 = this.toggle1.bind(this);
    this.toggleModalTutorial = this.toggleModalTutorial.bind(this);
    this.location = this.location.bind(this);
  }

  state = {
    buttonCopy: 'Copy Link Catalog',
    modalOpen1: false,
    modalTutorial: false,
    deferredPrompt: null,
    sidebarCollapsed: false,
    showScrollIndicator: true
  };

  location() {
    const location = useLocation();
    return location;
  }

  toggle1() {
    this.setState(prevState => ({
      modalOpen1: !prevState.modalOpen1
    }));
  }

  toggleModalTutorial() {
    this.setState(prevState => ({ modalTutorial: !prevState.modalTutorial }));
  }

  toggleSidebar = () => {
    this.setState(prevState => ({
      sidebarCollapsed: !prevState.sidebarCollapsed
    }));
  };

  handleCopy() {
    const txtId = 'profilelink';
    copyToClipboard(txtId);
    this.setState(() => ({
      buttonCopy: 'Copied!'
    }));
  }

  componentDidMount() {
    window.RootUI.initPluginYaybar();
    setTimeout(() => {}, 2000);
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.handleAppInstalled);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.handleAppInstalled);
  }

  /**
   * Retain the browser install event for the optional in-app install button.
   *
   * @param {Event} e browser install-prompt event
   */
  handleBeforeInstallPrompt = e => {
    // Do not call preventDefault here: it suppresses Chrome's native install
    // banner and requires a later, user-initiated call to e.prompt().
    this.setState({ deferredPrompt: e });
  };

  handleAppInstalled = () => {
    this.setState({ deferredPrompt: null });
  };

  handleInstallPrompt = () => {
    const { deferredPrompt } = this.state;
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then(choiceResult => {
      if (choiceResult.outcome === 'accepted') {
        // eslint-disable-next-line no-console
        console.log('User accepted the install prompt');
      } else {
        // eslint-disable-next-line no-console
        console.log('User dismissed the install prompt');
      }
      this.setState({ deferredPrompt: null });
    });
  };

  logOut() {
    //const { updateAuth } = this.props;
    if (!this.props?.currentUser?.isAffiliate) {
      window.localStorage.removeItem(`account-mode:${this.props?.currentUser?.id}`);
    }
    // Full account logout, two steps (both needed):
    //   1. POST /logout (Bearer token via the axios interceptor) stamps the global-
    //      logout epoch + revokes this refresh token. Token-authenticated, so it
    //      works even though REACT_APP_LOGOUT_URL is a different origin whose GET
    //      wouldn't carry the auth-domain idp_session cookie.
    //   2. Then clear local cookies and top-level navigate to GET /logout so the
    //      first-party idp_session cookie destroys the IdP session, and redirect to
    //      sign-in. Afterwards every Mayar platform must sign in again.
    axios.post(process.env.REACT_APP_LOGOUT_URL).finally(() => {
      deleteAuthCookies();
      window.location.href = `${process.env.REACT_APP_LOGOUT_URL}?redirect_uri=${encodeURIComponent(
        `${window.location.origin}/sign-in`
      )}`;
    });
  }

  isSubMenuHidden(navName) {
    let hiddenSubMenuList = [];

    if (!isPaidPlan(this.props?.currentUser?.account?.planType) || this.props?.currentUser?.account?.isPlanStopped) {
      const hiddenWhenPlanStopped = [
        'Penagihan Massal',
        'Kirim Uang Massal',
        'Payroll',
        'Data Karyawan',
        'Pembayaran Gaji',
        'Detail Analytics'
      ];

      hiddenSubMenuList.push(...hiddenWhenPlanStopped);
    }

    if (this?.props?.currentUser?.role === 'admin') {
      const sidebarMenu = getLatestRolePathConfig(this.props.currentUser, 'sidebarName');
      const settingMenu = getLatestRolePathConfig(this.props.currentUser, 'settingRoute');

      if (navName === 'Beranda' || (navName === 'Pengaturan' && settingMenu.length > 0)) {
        return false;
      }

      if (sidebarMenu.indexOf(navName) < 0) {
        return true;
      }
    }

    if (
      !this.props.accountMode ||
      (this.props.accountMode &&
        JSON.stringify(this.props.accountMode) !== '{}' &&
        this.props.accountMode === 'merchant')
    ) {
      const hiddenForMerchantMode = ['Produk Affiliate'];

      hiddenSubMenuList.push(...hiddenForMerchantMode);
    }

    // Hid for affiliator mode
    if (this.props.accountMode === 'affiliator') {
      const hiddenForAffiliatorMode = [
        'Pelanggan',
        'Transaksi',
        'Berlangganan',
        'Order',
        'Permintaan Pembayaran',
        'Penagihan',
        'Penagihan Massal',
        'Invoice',
        'Link Pembayaran',
        'Produk Fisik',
        'Produk Digital',
        'Webinar',
        'Event & Acara',
        'Coaching & Mentoring',
        'Penggalangan Dana',
        'Halaman Utama',
        'Qris Statis',
        'Kritik dan Saran',
        'Kelas Online (OD)',
        'Auto Responder',
        'Broadcast',
        'Anggota Affiliates',
        'Diskon dan Kupon',
        'Payroll',
        'Data Karyawan',
        'Pembayaran Gaji',
        'Kirim Uang',
        'Kirim Uang Massal',
        'Bill Payment',
        'Pembayaran Invoice',
        'Pembayaran Massal',
        'Capital / Pendanaan',
        'Audio Book',
        'E-Book',
        'Podcast',
        'Bundling',
        'Kelas Cohort / Bootcamp',
        'Halaman Utama',
        'Webhook',
        'Cross-sell',
        'Integrasi',
        'Refer a Friend',
        'Membership & SaaS',
        'Tulisan',
        'Affiliate',
        'Lisensi Software',
        'Tags',
        'Analytics',
        'Semua Produk',
        'Produk Berbasis Credit',
        'Web Komik',
        'Video DRM Billing',
        'Creator Support Page',
        'Mayar Credit'
      ];

      hiddenSubMenuList.push(...hiddenForAffiliatorMode);
    }

    if (!this.props?.currentUser?.account?.planType && this.props?.currentUser?.isAffiliate) {
      const hiddenAsAffiliate = [
        'Pelanggan',
        'Transaksi',
        'Berlangganan',
        'Order',
        'Permintaan Pembayaran',
        'Penagihan',
        'Penagihan Massal',
        'Invoice',
        'Link Pembayaran',
        'Produk Fisik',
        'Produk Digital',
        'Webinar',
        'Event & Acara',
        'Coaching & Mentoring',
        'Penggalangan Dana',
        'Halaman Utama',
        'Qris Statis',
        'Kritik dan Saran',
        'Kelas Online (OD)',
        'Auto Responder',
        'Broadcast',
        'Anggota Affiliates',
        'Diskon dan Kupon',
        'Payroll',
        'Data Karyawan',
        'Pembayaran Gaji',
        'Kirim Uang',
        'Kirim Uang Massal',
        'Bill Payment',
        'Pembayaran Invoice',
        'Pembayaran Massal',
        'Capital / Pendanaan',
        'Audio Book',
        'E-Book',
        'Podcast',
        'Bundling',
        'Kelas Cohort / Bootcamp',
        'Halaman Utama',
        'Webhook',
        'Cross-sell',
        'Integrasi',
        'Refer a Friend',
        'Membership & SaaS',
        'Lisensi Software',
        'Tags',
        'Analytics',
        'Semua Produk',
        'Produk Berbasis Credit',
        'Web Komik',
        'Video DRM Billing',
        'Creator Support Page',
        'Mayar Credit'
      ];
      hiddenSubMenuList.push(...hiddenAsAffiliate);
    }

    if (isSpecialAffiliate(this.props?.currentUser)) {
      hiddenSubMenuList = hiddenSubMenuList.filter(
        item => item !== 'Affiliate' && item !== 'Anggota Affiliates' && item !== 'Sebagai Affiliator'
      );
    }

    if (!isInternalUserOnly(this.props?.currentUser)) {
      hiddenSubMenuList.push(
        'Payroll',
        'Data Karyawan',
        'Pembayaran Gaji',
        'Kirim Uang Massal',
        'Bill Payment',
        'Pembayaran Invoice',
        'Pembayaran Massal',
        'Kirim Uang'
      );
    }

    return hiddenSubMenuList.includes(navName);
  }

  renderSubmenus(nav, returnObject = false) {
    let thereIsActive = false;

    const result = Object.keys(nav).map(url => {
      if (this.isSubMenuHidden(nav[url].name)) {
        return;
      }

      const data = nav[url];
      const isActive = window.location.hash === `#${url}`;
      let isOpened = false;

      if (isActive) {
        thereIsActive = true;
      }

      let sub = '';
      if (data.sub) {
        const subData = this.renderSubmenus(data.sub, true);
        sub = <ul className="yay-submenu dropdown-triangle">{subData.content}</ul>;

        if (subData.thereIsActive) {
          isOpened = true;
          thereIsActive = true;
        }
      }

      if (!this.props.currentUser.isProfileCompleted) {
        return;
      }

      return (
        <React.Fragment key={`${url}-${data.name}`}>
          {data.label ? <li className="yay-label">{data.label}</li> : ''}
          <li
            className={classnames({
              'yay-item-active': isActive,
              'yay-submenu-open': isOpened
            })}
          >
            {data.name ? (
              <NavLink
                to={data.sub ? '#' : url}
                className={data.sub ? 'yay-sub-toggle' : ''}
                data-tour-id={TOUR_IDS_BY_URL[url]}
              >
                {data.icon ? (
                  <>
                    {data.name === 'Upgrade ke Mayar Pro' ? (
                      <>
                        <span
                          className={
                            data.name === 'Upgrade ke Mayar Pro' ? 'text-success yay-icon' : 'text-dark yay-icon'
                          }
                        >
                          <Icon name={data.icon} />
                        </span>
                        <span className={data.name === 'Upgrade ke Mayar Pro' ? 'text-success' : 'text-dark'}>
                          {data.name}{' '}
                          {(data.name === 'Produk Fisik' ||
                            data.name === 'Analytics' ||
                            data.name === 'AI Landing Page Builder') && <span className="text-primary">(Beta)</span>}
                          {data.name === 'Auto Responder' && <span className="text-warning">(Segera)</span>}
                        </span>
                      </>
                    ) : (
                      <>
                        <span
                          className={data.name === 'Refer a Friend' ? 'text-primary yay-icon' : 'text-dark yay-icon'}
                        >
                          <Icon name={data.icon} />
                        </span>
                        <span className={data.name === 'Refer a Friend' ? 'text-primary' : 'text-dark'}>
                          {data.name}{' '}
                          {(data.name === 'Produk Fisik' ||
                            data.name === 'Analytics' ||
                            data.name === 'AI Landing Page Builder') && <span className="text-primary">(Beta)</span>}
                          {data.name === 'Auto Responder' && <span className="text-warning">(Segera)</span>}
                        </span>
                      </>
                    )}
                    <span className="rui-yaybar-circle" />
                  </>
                ) : (
                  <span className="rui-yaybar-submenu-text">
                    {data.name}{' '}
                    {(data.name === 'Kirim Uang / Top Up Massal' ||
                      data.name === 'Whatsapp' ||
                      data.name === 'AI Landing Page Builder') && <span className="text-primary">(Beta)</span>}
                  </span>
                )}
                {data.sub ? (
                  <span className="yay-icon-collapse">
                    <Icon name="chevron-right" strokeWidth="1" className="rui-icon-collapse" />
                  </span>
                ) : (
                  ''
                )}
              </NavLink>
            ) : (
              ''
            )}
            {sub}
          </li>
        </React.Fragment>
      );
    });

    if (returnObject) {
      return {
        content: result,
        thereIsActive
      };
    }

    return result;
  }

  render() {
    const { settings, location, currentUser } = this.props;
    const { buttonCopy, deferredPrompt } = this.state;

    // Use filtered navigation if available, otherwise fallback to original
    let navRoute = settings.navigation_sidebar;

    // Only use filtered navigation if currentUser is properly loaded
    if (currentUser && currentUser.id && settings.getFilteredNavigationSidebar) {
      const filteredNav = settings.getFilteredNavigationSidebar(currentUser);
      if (filteredNav && Object.keys(filteredNav).length > 0) {
        navRoute = filteredNav;
      }
    }

    const emailMerchant = currentUser?.email;

    /* whitelist email for using whatsapp official */
    const whitelistEmailForWhatsappFeature = [
      'teammayar@gmail.com',
      'teammayarx@gmail.com',
      'teammayarz@gmail.com',
      'mayarstaging@toroo.org',
      'mayartesting05@gmail.com',
      'talksmayar@gmail.com'
    ];

    // Check if developer-tools route exists and has sub property before accessing
    if (emailMerchant && !whitelistEmailForWhatsappFeature.includes(emailMerchant)) {
      if (
        navRoute['/developer-tools'] &&
        navRoute['/developer-tools'].sub &&
        navRoute['/developer-tools'].sub['/whatsapp-official']
      ) {
        delete navRoute['/developer-tools'].sub['/whatsapp-official'];
      }
    }

    // Only call analytics if available and currentUser exists
    if (window.analytics && typeof window.analytics.identify === 'function' && currentUser && currentUser.id) {
      window.analytics.identify(currentUser.id, {
        name: currentUser.name,
        email: currentUser.email,
        mobile: currentUser.mobile,
        paymeLink: currentUser.paymeLink,
        accountName: currentUser.account ? currentUser.account.name : null
      });
      window.analytics.page(location.pathname, location);
    }

    return (
      <>
        <div
          className={classnames(
            'yaybar rui-yaybar yay-hide-to-small yay-gestures',
            settings.sidebar_dark && !settings.night_mode ? 'rui-yaybar-dark' : '',
            settings.sidebar_static ? 'yay-static' : '',
            settings.sidebar_effect ? `yay-${settings.sidebar_effect}` : '',
            this.state.sidebarCollapsed ? 'sidebar-collapsed' : ''
          )}
        >
          <div className="yay-wrap-menu">
            <div className="yaybar-wrap" ref={this.sidebarRef} style={{ height: '100%' }}>
              {/* <ul>
                                <li>
                                    <div className="rui-navbar-brand ml-5">
                                        { settings.nav_logo_path ? (
                                            <Link to={ settings.nav_logo_url } className="rui-navbar-logo">
                                                <img src={ settings.night_mode || settings.nav_dark ? settings.nav_logo_white_path : settings.nav_logo_path } alt="" width={ settings.nav_logo_width } />
                                            </Link>
                                        ) : '' }
                                    </div>
                                </li>
                            </ul> */}

              <ul>
                {/* <li>
                                    <a href="../dashboard.html">
                                        <span
                                            className="yay-icon"
                                            dangerouslySetInnerHTML={ { __html: require( '!svg-inline-loader!../../../assets/images/logo-html-inherit.svg' ) } }
                                        />
                                        <span>Switch to HTML</span>
                                        <span className="rui-yaybar-circle"></span>
                                    </a>
                                </li> */}

                {this.renderSubmenus(navRoute)}
                {/* {this.renderSubmenus(settings.navigation_sidebar)} */}

                {/* <li className="yay-label">Logout</li> */}
                <li>
                  <NavLink to="#" onClick={this.logOut}>
                    <span className="yay-icon">
                      <Icon name="log-out" />
                    </span>
                    <span>Log Out</span>
                    <span className="rui-yaybar-circle"></span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <SidebarScrollIndicator sidebarRef={this.sidebarRef} deferredPrompt={deferredPrompt} />

          <AccountModeButton
            deferredPrompt={deferredPrompt}
            handleInstallPrompt={this.handleInstallPrompt}
            buttonCopy={buttonCopy}
            handleCopy={this.handleCopy}
          />

          <div className="rui-yaybar-icons">
            <Row className="no-gutters justify-content-around">
              <Col xs="auto">
                <a
                  href="https://feedback.mayar.id/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-custom-round"
                  data-sleek-changelog
                  data-badge-changelog
                >
                  <Icon name="bell" />
                </a>
              </Col>
              <Col xs="auto">
                <Button className="btn btn-custom-round" onClick={this.toggleModalTutorial}>
                  <Icon name="help-circle" />
                </Button>
              </Col>
              <Col xs="auto">
                <Link to="#" onClick={this.logOut} className="btn btn-custom-round">
                  <Icon name="log-out" />
                </Link>
              </Col>
            </Row>
          </div>
        </div>
        <div className="rui-yaybar-bg" />
        <Modal isOpen={this.state.modalOpen1} fade>
          <div className="modal-header">
            <h5 className="modal-title h2">Tombol Overlay Embed</h5>
            <Button className="close" color="" onClick={this.toggle1}>
              <Icon name="x" />
            </Button>
          </div>
          <ModalBodyEmbedButton url={paymeUrl(this.props.currentUser.paymeLink)} />
        </Modal>
        <ModalTutorial isOpen={this.state.modalTutorial} onClose={this.toggleModalTutorial} />
        <button
          type="button"
          className="tablet-sidebar-toggle"
          style={{
            bottom: '100px',
            top: 'auto',
            transform: 'none',
            backgroundColor: '#f8f9fa',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1px solid #e9ecef',
            width: '40px',
            height: '40px',
            left: this.state.sidebarCollapsed ? '50px' : '220px',
            transition: 'left 0.3s ease-in-out'
          }}
          onClick={this.toggleSidebar}
          aria-label="Toggle Sidebar"
        >
          <span className="toggle-icon">
            <Icon name={this.state.sidebarCollapsed ? 'chevron-right' : 'chevron-left'} />
          </span>
        </button>
      </>
    );
  }
}

export default connect(
  ({ settings, currentUser, accountMode }) => ({
    settings,
    currentUser,
    accountMode
  }),
  { updateAuth: actionUpdateAuth, updateCurUser: updateCurrentUser, changeModeAccount: changeModeAccount }
)(PageYaybar);
